'use client'

import { Markdown } from '@/lib/ui-kit/components/Markdown.component'
import { FeatureList } from '@/lib/api-connector-strapi-cms/api/reeve-landing-page/reeveLandingPageApi.types'
import { motion, useInView } from 'framer-motion'
import { Caption } from '@/modules/reeve/components/Caption.component'
import Image from 'next/image'
import { useRef } from 'react'

interface FeaturesProps {
  caption: string
  heading: string
  featureList: FeatureList[]
  apiAssetsUrl?: string
}

export const Features = ({
  caption,
  heading,
  featureList,
  apiAssetsUrl,
}: FeaturesProps) => {
  return (
    <section
      id="features"
      className="m-auto flex max-w-[1440px] flex-col items-start justify-center overflow-hidden px-6 pb-0 pt-20 sm:items-center sm:pt-24 xl:px-10 xl:pb-12 xl:pt-32 2xl:pb-20 2xl:pt-60"
    >
      <Caption text={caption} variant="gray" />
      <div className="mb-0 pt-4 text-[2rem] font-normal leading-[2.5rem] text-reeve-primary sm:pt-6 sm:text-[2.5rem] sm:leading-[3rem] xl:mb-20">
        <Markdown>{heading}</Markdown>
      </div>
      {featureList.map((feature, index) => (
        <FeatureItem
          key={index}
          feature={feature}
          index={index}
          apiAssetsUrl={apiAssetsUrl}
        />
      ))}
    </section>
  )
}

const FeatureItem = ({
  feature,
  index,
  apiAssetsUrl,
}: {
  feature: FeatureList
  index: number
  apiAssetsUrl?: string
}) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })

  const textContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  }

  const textItemVariants = {
    hidden: {
      y: 30,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
        mass: 1,
      },
    },
  }

  const imageVariants = {
    hidden: {
      scale: 0.9,
      opacity: 0,
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 80,
        damping: 15,
        mass: 1.2,
        delay: 0.6,
      },
    },
  }

  return (
    <div
      ref={ref}
      className="group grid w-full grid-cols-12 gap-y-8 border-b border-reeve-divider py-12 last:border-b-0 sm:py-16 xl:border-b-0 xl:py-20"
    >
      <motion.div
        className="col-span-12 flex w-full flex-col items-start justify-start xl:col-span-5 xl:group-odd:col-start-2 xl:group-even:order-2 xl:group-even:col-start-7"
        variants={textContainerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        <motion.div
          className="mb-3 rounded-lg bg-reeve-divider p-2 sm:mb-4 xl:mb-8 xl:p-3"
          variants={textItemVariants}
        >
          <Image
            className="h-7 w-7 sm:h-8 sm:w-8 xl:h-10 xl:w-10"
            src={`${apiAssetsUrl || ''}${feature.icon.url}`}
            width={feature.icon.width}
            height={feature.icon.height}
            alt={feature.icon.alternativeText || `${feature.heading} icon`}
          />
        </motion.div>
        <motion.div
          className="mb-4 text-[1.5rem] font-normal leading-[2rem] text-reeve-primary sm:mb-6 xl:mb-16 xl:text-[2rem] xl:leading-[2.25rem]"
          variants={textItemVariants}
        >
          <Markdown>{feature.heading}</Markdown>
        </motion.div>
        <motion.div
          className={`max-w-2xl text-[1.125rem] font-normal leading-[1.5rem] text-reeve-secondary-gray xl:max-w-full xl:text-[1.25rem] xl:leading-[1.75rem] ${
            feature.bulletPoint &&
            feature.bulletPoint?.length > 0 &&
            'mb-4 xl:mb-6'
          }`}
          variants={textItemVariants}
        >
          <Markdown>{feature.subHeading}</Markdown>
        </motion.div>
        {feature.bulletPoint &&
          feature.bulletPoint.map((point, index) => (
            <motion.div
              key={index}
              className="mb-2 flex max-w-2xl items-center gap-x-2 text-[1.125rem] font-normal leading-[1.5rem] text-reeve-secondary-gray xl:mb-4 xl:max-w-full xl:text-[1.25rem] xl:leading-[1.75rem]"
              variants={textItemVariants}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="fill-current size-5 xl:size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
              <Markdown>{point.text}</Markdown>
            </motion.div>
          ))}
      </motion.div>
      <motion.div
        className="col-span-12 flex items-center justify-center px-4 xl:col-span-6 xl:group-even:order-1 xl:group-even:col-start-1"
        variants={imageVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        <Image
          className="h-auto w-auto"
          src={`${apiAssetsUrl || ''}${feature.image.url}`}
          width={feature.image.width}
          height={feature.image.height}
          alt={feature.image.alternativeText || `${feature.heading} image`}
        />
      </motion.div>
    </div>
  )
}
